<script setup>
/**
 * SzPermissionsGate — Modal bloqueante de 1º acesso que destrava notificações
 * sonoras + push. Captura o gesto do usuário para:
 *   1) Notification.requestPermission (necessário em todo browser)
 *   2) Audio warm-up (destrava autoplay policy de Chrome/Safari/FF)
 *   3) Push subscription (VAPID) se Notification granted
 *   4) Persiste defaults ON em ui_settings + sz_permissions_onboarded_at
 *
 * Marker: SZ_PERMISSIONS_GATE_v1_20260519
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { requestPushPermissions } from 'dashboard/helper/pushHelper';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import { initializeAudioAlerts } from 'dashboard/helper/scriptHelpers';

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const visible = ref(false);
const checking = ref(true);
const step = ref('intro'); // 'intro' | 'denied'
const notificationPermission = ref('default');
const inProgress = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);

const hasOnboarded = computed(
  () => !!uiSettings.value?.sz_permissions_onboarded_at
);

function detectPermission() {
  if ('Notification' in window) {
    notificationPermission.value = Notification.permission;
  }
}

function checkShouldShow() {
  if (!currentUser.value || !currentUser.value.id) return;
  detectPermission();

  // Reabre o modal se a permissão do browser não está 'granted',
  // mesmo que o usuário já tenha completado o onboarding antes.
  // Cobre: reset de permissões no browser, troca de máquina, aba anônima.
  const notifGranted =
    'Notification' in window && Notification.permission === 'granted';

  if (hasOnboarded.value && notifGranted) {
    visible.value = false;
    checking.value = false;
    return;
  }

  visible.value = true;
  checking.value = false;
}

async function handleEnable() {
  if (inProgress.value) return;
  inProgress.value = true;
  try {
    // 1) Audio warm-up SÍNCRONO dentro do click handler (Safari requirement)
    try {
      DashboardAudioNotificationHelper.warmup();
    } catch (e) {
      // non-fatal — warmup pode falhar e o som ainda funciona em browsers permissivos
      // eslint-disable-next-line no-console
      console.warn('[SzPermissionsGate] warmup error:', e);
    }

    // 2) Notification permission (precisa de gesto do user)
    let permission = 'default';
    if ('Notification' in window) {
      permission = await Notification.requestPermission();
      notificationPermission.value = permission;
    }

    // 3) Push subscription via Service Worker se notification granted
    if (permission === 'granted') {
      try {
        requestPushPermissions({ onSuccess: () => {} });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SzPermissionsGate] push subscription error:', e);
      }
    }

    // 4) Persistir timestamp em ui_settings.
    // Defaults ON (som=all, always_play=true) só na PRIMEIRA vez — em
    // re-onboard (user resetou permissão do browser), preserva as
    // preferências de som que ele já customizou em Settings.
    const isFirstOnboard = !hasOnboarded.value;
    const nextSettings = {
      sz_permissions_onboarded_at: new Date().toISOString(),
    };
    if (isFirstOnboard) {
      // 'assigned' = só conversas atribuídas ao usuário (não toca pra todas as inboxes)
      nextSettings.enable_audio_alerts = 'assigned';
      nextSettings.always_play_audio_alert = true;
      nextSettings.alert_if_unread_assigned_conversation_exist = false;
      if (!uiSettings.value?.notification_tone) {
        nextSettings.notification_tone = 'ding';
      }
    }
    await updateUISettings(nextSettings);

    // 5) Re-aplicar config no helper de áudio com os novos defaults
    if (currentUser.value) {
      const mergedUser = {
        ...currentUser.value,
        ui_settings: {
          ...(currentUser.value.ui_settings || {}),
          ...nextSettings,
        },
      };
      initializeAudioAlerts(mergedUser);
    }

    if (permission === 'denied') {
      step.value = 'denied';
    } else {
      // granted ou default (modal fechado sem decisão) — onboarding cumprido
      visible.value = false;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SzPermissionsGate] handleEnable error:', e);
    step.value = 'denied';
  } finally {
    inProgress.value = false;
  }
}

async function recheckPermission() {
  detectPermission();
  if (notificationPermission.value === 'granted') {
    try {
      requestPushPermissions({ onSuccess: () => {} });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SzPermissionsGate] recheck push error:', e);
    }
    step.value = 'intro';
    visible.value = false;
  }
}

function browserHint() {
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) {
    return 'Clique no ícone de cadeado (à esquerda da URL) → Permissões deste site → Notificações → Permitir.';
  }
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
    return 'Clique no ícone de cadeado (à esquerda da URL) → Notificações → Permitir.';
  }
  if (/Firefox\//.test(ua)) {
    return 'Clique no ícone de escudo/cadeado (à esquerda da URL) → Permissões → Notificações → Permitir.';
  }
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    return 'Safari → Ajustes → Sites → Notificações → habilite tokyo.solarzdashboard.com.br.';
  }
  return 'Procure pelas configurações de permissões do site no seu navegador e libere Notificações.';
}

onMounted(() => {
  // 2s de atraso pra não competir visualmente com o SzMfaGate em montagem simultânea
  setTimeout(checkShouldShow, 2000);
});

watch(currentUser, (newVal, oldVal) => {
  if (newVal && newVal.id && (!oldVal || newVal.id !== oldVal.id)) {
    checkShouldShow();
  }
});

watch(uiSettings, () => {
  if (visible.value && hasOnboarded.value) {
    visible.value = false;
  }
});
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="sz-perm-gate">
      <div class="sz-perm-gate-backdrop" />
      <div class="sz-perm-gate-content">
        <div v-if="step === 'intro'">
          <div class="sz-perm-gate-header">
            <div class="sz-perm-gate-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <h2>Ative as notificações</h2>
            <p>Para receber alertas sonoros e visuais de novas mensagens, precisamos da sua permissão. Leva 2 segundos.</p>
          </div>

          <div class="sz-perm-gate-features">
            <div class="sz-perm-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Som ao receber mensagens novas</span>
            </div>
            <div class="sz-perm-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Notificações do navegador mesmo com a aba minimizada</span>
            </div>
            <div class="sz-perm-gate-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sz-check-icon"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Pode desligar a qualquer momento nas preferências</span>
            </div>
          </div>

          <button class="sz-perm-gate-btn" :disabled="inProgress" @click="handleEnable">
            {{ inProgress ? 'Ativando…' : 'Ativar notificações' }}
          </button>
          <p class="sz-perm-gate-hint">
            Seu navegador vai mostrar um prompt no topo da tela. Clique em <strong>Permitir</strong>.
          </p>
        </div>

        <div v-else-if="step === 'denied'">
          <div class="sz-perm-gate-header">
            <div class="sz-perm-gate-icon sz-perm-gate-icon-warn">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h2>Notificações bloqueadas</h2>
            <p>Você bloqueou as notificações. Para ativar, libere manualmente:</p>
          </div>

          <div class="sz-perm-gate-instructions">
            {{ browserHint() }}
          </div>

          <button class="sz-perm-gate-btn" @click="recheckPermission">
            Verificar novamente
          </button>
          <button class="sz-perm-gate-btn sz-perm-gate-btn-ghost" :disabled="inProgress" @click="handleEnable">
            Tentar de novo
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style>
/* Não scoped — Teleport renderiza fora da árvore, dark selectors precisam casar */

.sz-perm-gate {
  position: fixed;
  inset: 0;
  z-index: 999998; /* abaixo do SzMfaGate (999999) pra MFA sempre cobrir */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.sz-perm-gate-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(10px);
}

.sz-perm-gate-content {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.dark .sz-perm-gate-content {
  background: #262629 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.sz-perm-gate-header {
  text-align: center;
  margin-bottom: 24px;
}

.sz-perm-gate-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #EEF6F0;
  color: #388E3C;
  margin-bottom: 16px;
}

.sz-perm-gate-icon-warn {
  background: #FEF3C7;
  color: #B45309;
}

.dark .sz-perm-gate-icon {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #4CAF50 !important;
}

.dark .sz-perm-gate-icon-warn {
  background: rgba(245, 158, 11, 0.18) !important;
  color: #F59E0B !important;
}

.sz-perm-gate-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px;
}

.dark .sz-perm-gate-header h2 {
  color: #F3F4F6 !important;
}

.sz-perm-gate-header p {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  line-height: 1.5;
}

.dark .sz-perm-gate-header p {
  color: #9CA3AF !important;
}

.sz-perm-gate-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.dark .sz-perm-gate-features {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.sz-perm-gate-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #374151;
}

.dark .sz-perm-gate-feature {
  color: #D1D5DB !important;
}

.sz-check-icon {
  flex-shrink: 0;
  color: #388E3C;
}

.dark .sz-check-icon {
  color: #4CAF50 !important;
}

.sz-perm-gate-btn {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: none;
  background: #388E3C;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  margin-top: 8px;
}

.sz-perm-gate-btn:hover:not(:disabled) {
  background: #2E7D32;
}

.sz-perm-gate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sz-perm-gate-btn-ghost {
  background: transparent;
  color: #388E3C;
  border: 1px solid #388E3C;
}

.sz-perm-gate-btn-ghost:hover:not(:disabled) {
  background: rgba(56, 142, 60, 0.08);
}

.dark .sz-perm-gate-btn {
  background: #4CAF50 !important;
}

.dark .sz-perm-gate-btn:hover:not(:disabled) {
  background: #388E3C !important;
}

.dark .sz-perm-gate-btn-ghost {
  background: transparent !important;
  color: #4CAF50 !important;
  border-color: #4CAF50 !important;
}

.sz-perm-gate-hint {
  font-size: 12px;
  color: #6B7280;
  margin-top: 12px;
  text-align: center;
  line-height: 1.5;
}

.dark .sz-perm-gate-hint {
  color: #9CA3AF !important;
}

.sz-perm-gate-instructions {
  padding: 16px;
  background: #F9FAFB;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 16px;
}

.dark .sz-perm-gate-instructions {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: #D1D5DB !important;
}

.sz-perm-gate-content::-webkit-scrollbar {
  width: 6px;
}
.sz-perm-gate-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 3px;
}
.dark .sz-perm-gate-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
}
</style>